import type { SalaryCurrency } from '@seek/salary-data/lib-types/types';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  FieldLabel,
  FieldMessage,
  Stack,
  Text,
  TextField,
} from 'braid-design-system';
import type React from 'react';

import {
  MAX_SALARY,
  MIN_SALARY,
} from 'src/app/shared/constants/formDataFields';
import { SalaryCurrency as SalaryCurrencyComponent } from 'src/app/v1/views/components/Pay/components/SalaryCurrency';
import { ContextualHelp } from 'src/app/v1/views/shared/ContextualHelp';
import type { SuggestedSalary } from 'src/types/salaryTypes';

import translations from './.vocab';

type PayRangeProps = {
  handleSalaryCurrencyOnChange: (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
  minSalaryText: string;
  onMinSalaryFieldBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onMinSalaryFieldChange: (e: React.FormEvent<HTMLInputElement>) => void;
  minSalaryFieldMessage: string | null | undefined;
  salaryError: string | null | undefined;
  maxSalaryText: string;
  onMaxSalaryFieldBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onMaxSalaryFieldChange: (e: React.FormEvent<HTMLInputElement>) => void;
  maxSalaryFieldMessage: string | null | undefined;
  jobTitle: string;
  currency: SalaryCurrency;
  onToggleContextualHelp: () => void;
  suggestedSalary: SuggestedSalary | null;
};

export const PayRange = (props: PayRangeProps) => {
  const { t } = useTranslations(translations);
  const description = t('pay-range-description');
  const maxSalaryFieldLabel = t('pay-range-max-label');
  const maxSalaryFieldPlaceholder = t('pay-range-max-placeholder');
  const minSalaryFieldLabel = t('pay-range-header');
  const minSalaryFieldLabelText = t('pay-range-min-label');
  const minSalaryFieldPlaceholder = t('pay-range-min-placeholder');
  const contextualHelpMessage = t('pay-range-contextual-help');

  return (
    <Stack space="small">
      <Box position="relative">
        <FieldLabel
          htmlFor={MIN_SALARY}
          label={
            <>
              {minSalaryFieldLabel}{' '}
              <ContextualHelp
                ariaLabel="Pay Range Help"
                tooltipId="pay-range-help-tooltip"
                message={<Text>{contextualHelpMessage}</Text>}
                onTrigger={props.onToggleContextualHelp}
              />
            </>
          }
          description={description}
        />
      </Box>

      <Stack space="xsmall">
        <Columns space="small" collapseBelow="tablet">
          <Column width="1/5">
            <SalaryCurrencyComponent
              currency={props.currency}
              handleSalaryCurrencyOnChange={props.handleSalaryCurrencyOnChange}
            />
          </Column>
          <Column width="2/5">
            <TextField
              id={MIN_SALARY}
              type="text"
              value={props.minSalaryText || ''}
              onBlur={props.onMinSalaryFieldBlur}
              onChange={props.onMinSalaryFieldChange}
              placeholder={minSalaryFieldPlaceholder}
              label={minSalaryFieldLabelText}
              message={props.minSalaryFieldMessage}
              tone={
                props.salaryError || props.minSalaryFieldMessage
                  ? 'critical'
                  : 'neutral'
              }
              reserveMessageSpace={!props.salaryError}
              autoComplete="off"
            />
          </Column>
          <Column width="2/5">
            <TextField
              id={MAX_SALARY}
              type="text"
              value={props.maxSalaryText || ''}
              onBlur={props.onMaxSalaryFieldBlur}
              onChange={props.onMaxSalaryFieldChange}
              placeholder={maxSalaryFieldPlaceholder}
              label={maxSalaryFieldLabel}
              message={props.maxSalaryFieldMessage}
              tone={
                props.salaryError || props.maxSalaryFieldMessage
                  ? 'critical'
                  : 'neutral'
              }
              reserveMessageSpace={!props.salaryError}
              autoComplete="off"
            />
          </Column>
        </Columns>
        {props.salaryError ? (
          <FieldMessage
            id="payRangeFieldMessage"
            message={props.salaryError}
            tone={props.salaryError ? 'critical' : 'neutral'}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};
